import { createAppRequest } from '@/services/request';

const request = createAppRequest();

export type AdmissionApplicationType =
  | 'INITIAL'
  | 'ADDITIONAL_COMPANY'
  | 'ADDITIONAL_CATEGORY'
  | 'CATEGORY_REVIEW'
  | 'BASE_REVIEW';
export type AdmissionApplicationStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'WITHDRAWN'
  | 'REVIEW_COMPLETED';
export type AdmissionDecision = 'APPROVED' | 'PARTIALLY_APPROVED' | 'REJECTED';
export type CompanyDecision = 'APPROVED' | 'REJECTED' | 'NEEDS_SUPPLEMENT';
export type CategoryDecision = 'APPROVED' | 'REJECTED' | 'NEEDS_SUPPLEMENT';
export type ProviderQualificationStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'EXITED'
  | 'REVOKED';
export type CompanyEntityStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'EXITED';
export type CategoryQualificationStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'REVOKED'
  | 'EXITED';

export interface ServiceProviderAdmissionOverview {
  provider: {
    displayName: string;
    qualificationStatus: ProviderQualificationStatus;
    profileSnapshot: Record<string, unknown> | null;
  } | null;
  latestApplication: ServiceAdmissionApplicationData | null;
  companies: ServiceProviderCompanyEntityData[];
  qualifications: ServiceProviderCategoryQualificationData[];
}

export interface ServiceAdmissionApplicationData {
  uuid: string;
  applicationNo: string | null;
  applicationType: AdmissionApplicationType;
  tenantUuid: string;
  companyEntityUuid: string | null;
  providerCompanyEntityUuid: string | null;
  status: AdmissionApplicationStatus;
  roundNo: number;
  parentApplicationUuid: string | null;
  submittedSnapshot: Record<string, unknown> | null;
  lockVersion: number;
  createdAt: string;
  updatedAt: string;
  categories?: ServiceAdmissionApplicationCategoryData[];
  attachments?: ServiceAdmissionAttachmentData[];
}

export interface ServiceAdmissionApplicationCategoryData {
  uuid: string;
  applicationUuid: string;
  businessTypeUuid: string;
  ruleVersionUuid: string | null;
  fieldSnapshot: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceAdmissionAttachmentData {
  uuid: string;
  applicationUuid: string;
  categoryUuid: string | null;
  fileReference: string;
  version: number;
  isCurrent: boolean;
  metadataSummary?: Record<string, unknown> | null;
  ruleMappings?: Array<{ categoryUuid: string; ruleKey: string }>;
  createdAt: string;
}

export interface ServiceProviderCompanyEntityData {
  uuid: string;
  companyEntityUuid: string;
  status: CompanyEntityStatus;
  companySnapshot: Record<string, unknown> | null;
  qualifications: ServiceProviderCategoryQualificationData[];
  createdAt: string;
  updatedAt: string;
}

export interface ServiceProviderCategoryQualificationData {
  uuid: string;
  companyEntityUuid: string;
  businessTypeUuid: string;
  status: CategoryQualificationStatus;
  statusVersion: number;
  validUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdmissionRuleFieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'date';
  required?: boolean;
  description?: string;
}

export interface AdmissionRuleAttachmentConfig {
  key: string;
  label: string;
  required?: boolean;
  description?: string;
  qualificationCode?: string;
}

export interface AdmissionCategoryData {
  uuid: string;
  code: string;
  name: string;
  parentUuid: string | null;
  level: number;
  isEnabled: boolean;
  sortOrder: number;
}

export interface AdmissionMaterialRequirement {
  qualificationCode: string;
  name: string;
  requirementType: 'PLATFORM_CAPABILITY' | 'LEGAL_REQUIREMENT' | string;
  description: string | null;
  required: boolean;
  mappings: Array<{ businessTypeUuid: string; ruleKey: string }>;
  ruleVersionUuids: string[];
}

export interface AdmissionBusinessTypeData {
  uuid: string;
  code: string;
  secondaryCategoryUuid?: string | null;
  isEnabled: boolean;
  currentRule?: {
    uuid: string;
    businessTypeUuid: string;
    version: number;
    status: string;
    rules: {
      fields?: AdmissionRuleFieldConfig[];
      attachments?: AdmissionRuleAttachmentConfig[];
      [key: string]: unknown;
    };
    effectiveAt: string | null;
  } | null;
}

export interface CompanyEntityProfileData {
  uuid: string;
  tenantUuid: string;
  snapshot: {
    uuid: string;
    name: string;
    creditCode: string;
    legalRepresentative?: string;
    entityType?: string;
    status?: string;
    registeredAddress?: string;
    [key: string]: unknown;
  };
}

export interface ServiceAdmissionHistoryData {
  application: ServiceAdmissionApplicationData;
  reviews: Array<{
    uuid: string;
    applicationUuid: string;
    reviewBatchUuid: string;
    overallDecision: AdmissionDecision;
    decisionSummary: {
      providerDecision?: CompanyDecision | null;
      companyDecision?: CompanyDecision | null;
      overallDecision?: AdmissionDecision | null;
      comment?: string | null;
      providerComment?: string | null;
      companyComment?: string | null;
      categoryDecisions: Array<{
        businessTypeUuid: string;
        decision: CategoryDecision;
        reason?: string | null;
      }>;
    };
    createdAt: string;
  }>;
  attachments: ServiceAdmissionAttachmentData[];
  auditEvents: Array<{
    uuid: string;
    action: string;
    source: string;
    result: string;
    reason?: string | null;
    changeSummary?: Record<string, unknown> | null;
    createdAt: string;
  }>;
}

export interface UploadFileResult {
  fileReference: string;
  fileName: string;
  size: number;
  mimeType: string | null;
  appId: string;
  createdAt: string;
  applicationUuid: string | null;
  categoryUuid: string | null;
}

export interface FileUrlResult {
  url: string;
  urlMode: 'public_direct' | 'signed';
}

export interface CreateAdmissionDraftDto {
  companyEntityUuid?: string;
  displayName?: string;
  providerProfile?: Record<string, unknown>;
  businessTypeUuids?: string[];
  categoryFields?: Record<string, Record<string, unknown>>;
}

export interface UpdateAdmissionDraftDto {
  version: number;
  companyEntityUuid?: string;
  displayName?: string;
  providerProfile?: Record<string, unknown>;
  businessTypeUuids?: string[];
  categoryFields?: Record<string, Record<string, unknown>>;
}

export interface SubmitAdmissionApplicationDto {
  lockVersion?: number;
  companyEntityUuid?: string;
  displayName?: string;
  providerProfile?: Record<string, unknown>;
  businessTypeUuids?: string[];
  categoryFields?: Record<string, Record<string, unknown>>;
  attachmentUuids?: string[];
}

export interface WithdrawAdmissionApplicationDto {
  reason?: string;
}

export interface ResubmitAdmissionApplicationDto {
  displayName?: string;
  providerProfile?: Record<string, unknown>;
  companyEntityUuid?: string;
  businessTypeUuids?: string[];
  affectedBusinessTypeUuids?: string[];
  affectedReviewParts?: Array<'PROVIDER_PROFILE' | 'COMPANY_PROFILE'>;
  categoryFields?: Record<string, Record<string, unknown>>;
  reason?: string;
}

export interface RecordAttachmentDto {
  categoryUuid?: string;
  reviewPart?: 'PROVIDER_PROFILE' | 'COMPANY_PROFILE';
  fileReference: string;
  metadataSummary?: Record<string, unknown>;
  ruleMappings?: Array<{ businessTypeUuid: string; ruleKey: string }>;
}

export interface CreateAdditionalCompanyApplicationDto {
  businessTypeUuids: string[];
  categoryFields?: Record<string, Record<string, unknown>>;
}

export interface CreateAdditionalCategoryApplicationDto {
  businessTypeUuids: string[];
  categoryFields?: Record<string, Record<string, unknown>>;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  requestId?: string;
}

const unwrap = <T>(res: Envelope<T>): T => res.data;

/** 获取服务商准入概览与最新状态 */
export const getAdmissionStatus =
  async (): Promise<ServiceProviderAdmissionOverview> => {
    const res = await request<Envelope<ServiceProviderAdmissionOverview>>(
      '/bff/service-provider/admission',
    );
    return unwrap(res);
  };

/** 创建首次入驻申请草稿 */
export const createAdmissionDraft = async (
  dto: CreateAdmissionDraftDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    '/bff/service-provider/admission',
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 更新申请草稿 */
export const updateAdmissionDraft = async (
  applicationUuid: string,
  dto: UpdateAdmissionDraftDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/admission/${applicationUuid}`,
    {
      method: 'PATCH',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 提交入驻申请 */
export const submitAdmissionApplication = async (
  applicationUuid: string,
  dto: SubmitAdmissionApplicationDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/admission/${applicationUuid}/submit`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 撤回入驻申请 */
export const withdrawAdmissionApplication = async (
  applicationUuid: string,
  dto: WithdrawAdmissionApplicationDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/admission/${applicationUuid}/withdraw`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 驳回/撤回后重新提交申请 */
export const resubmitAdmissionApplication = async (
  applicationUuid: string,
  dto: ResubmitAdmissionApplicationDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/admission/${applicationUuid}/resubmit`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 获取申请历史记录及审计 */
export const getAdmissionHistory = async (
  applicationUuid: string,
): Promise<ServiceAdmissionHistoryData> => {
  const res = await request<Envelope<ServiceAdmissionHistoryData>>(
    `/bff/service-provider/admission/${applicationUuid}/history`,
  );
  return unwrap(res);
};

/** 登记资质附件 */
export const recordAdmissionAttachment = async (
  applicationUuid: string,
  dto: RecordAttachmentDto,
): Promise<ServiceAdmissionAttachmentData> => {
  const res = await request<Envelope<ServiceAdmissionAttachmentData>>(
    `/bff/service-provider/admission/${applicationUuid}/attachments`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 作废当前资质附件，保留业务附件版本与审计记录 */
export const invalidateAdmissionAttachment = async (
  applicationUuid: string,
  attachmentUuid: string,
): Promise<ServiceAdmissionAttachmentData> => {
  const res = await request<Envelope<ServiceAdmissionAttachmentData>>(
    `/bff/service-provider/admission/${applicationUuid}/attachments/${attachmentUuid}`,
    { method: 'DELETE' },
  );
  return unwrap(res);
};

/** 获取可用业务类型及其当前有效规则 */
export const listAvailableBusinessTypes = async (): Promise<
  AdmissionBusinessTypeData[]
> => {
  const res = await request<Envelope<AdmissionBusinessTypeData[]>>(
    '/bff/service-provider/admission/business-types',
  );
  return unwrap(res);
};

/** 获取当前可申报的两级服务类目目录 */
export const listAvailableAdmissionCategories = async (): Promise<
  AdmissionCategoryData[]
> => {
  const res = await request<Envelope<AdmissionCategoryData[]>>(
    '/bff/service-provider/admission/categories',
  );
  return unwrap(res);
};

/** 按已选类目汇总去重后的材料清单 */
export const getAdmissionMaterialRequirements = async (
  businessTypeUuids: string[],
): Promise<AdmissionMaterialRequirement[]> => {
  const res = await request<Envelope<AdmissionMaterialRequirement[]>>(
    '/bff/service-provider/admission/material-requirements',
    { method: 'POST', data: { businessTypeUuids } },
  );
  return unwrap(res);
};

/** 获取当前租户已关联主体列表 */
export const listCompanyEntities = async (): Promise<
  ServiceProviderCompanyEntityData[]
> => {
  const res = await request<Envelope<ServiceProviderCompanyEntityData[]>>(
    '/bff/service-provider/company-entities',
  );
  return unwrap(res);
};

/** 获取主体详情及资质 */
export const getCompanyEntityDetail = async (
  companyEntityUuid: string,
): Promise<ServiceProviderCompanyEntityData> => {
  const res = await request<Envelope<ServiceProviderCompanyEntityData>>(
    `/bff/service-provider/company-entities/${companyEntityUuid}`,
  );
  return unwrap(res);
};

/** 从企业档案真实接口核验主体归属 */
export const verifyCompanyEntity = async (
  companyEntityUuid: string,
): Promise<CompanyEntityProfileData> => {
  const res = await request<Envelope<CompanyEntityProfileData>>(
    `/bff/service-provider/company-entity-verify/${companyEntityUuid}`,
  );
  return unwrap(res);
};

/** 查询当前租户在企业档案中的主体选项。 */
export const listAvailableCompanyEntityProfiles = async (): Promise<
  CompanyEntityProfileData[]
> => {
  const res = await request<Envelope<CompanyEntityProfileData[]>>(
    '/bff/service-provider/company-entity-options',
  );
  return unwrap(res);
};

/** 申请新增主体认证 */
export const applyAdditionalCompany = async (
  companyEntityUuid: string,
  dto: CreateAdditionalCompanyApplicationDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/company-entities/${companyEntityUuid}/applications`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

/** 为主体申请新增业务类目 */
export const applyAdditionalCategories = async (
  companyEntityUuid: string,
  dto: CreateAdditionalCategoryApplicationDto,
): Promise<ServiceAdmissionApplicationData> => {
  const res = await request<Envelope<ServiceAdmissionApplicationData>>(
    `/bff/service-provider/company-entities/${companyEntityUuid}/category-applications`,
    {
      method: 'POST',
      data: dto,
    },
  );
  return unwrap(res);
};

// ==========================================
// 通用文件管理接口
// ==========================================

/** 上传文件至通用文件 BFF */
export const uploadServiceFile = async (
  file: File,
  options?: { applicationUuid?: string; categoryUuid?: string },
): Promise<UploadFileResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.applicationUuid)
    formData.append('applicationUuid', options.applicationUuid);
  if (options?.categoryUuid)
    formData.append('categoryUuid', options.categoryUuid);

  return request<UploadFileResult>('/bff/files', {
    method: 'POST',
    data: formData,
  });
};

/** 清理当前登录人 24 小时前仍未关联申请的入驻附件 */
export const cleanupUnassociatedAdmissionUploads = async (): Promise<{ deletedCount: number }> => {
  return request<{ deletedCount: number }>('/bff/files/admission-orphans/cleanup', {
    method: 'POST',
  });
};

/** 获取文件下载 URL */
export const getFileDownloadUrl = async (
  fileReference: string,
): Promise<FileUrlResult> => {
  return request<FileUrlResult>(
    `/bff/files/${encodeURIComponent(fileReference)}/download`,
  );
};

/** 获取文件预览 URL */
export const getFilePreviewUrl = async (
  fileReference: string,
): Promise<FileUrlResult> => {
  return request<FileUrlResult>(
    `/bff/files/${encodeURIComponent(fileReference)}/preview`,
  );
};

/** 删除已上传文件 */
export const deleteServiceFile = async (
  fileReference: string,
): Promise<{ success: boolean }> => {
  return request<{ success: boolean }>(
    `/bff/files/${encodeURIComponent(fileReference)}`,
    {
      method: 'DELETE',
    },
  );
};
